// Deal detail as right-hand drawer (prototype, spec 10): stage, next step, key data, volume, and all mails of the
// deal in chronological order with original, attachments and their summaries. "👎 falsch zugeordnet" on automatic
// assignments asks where the mail belongs (the correction teaches the AI); "Bestätigen" settles a review.
// "Antworten" (spec 6.2a, 8): Claude drafts, the user edits and picks files; with a monitored own mailbox
// (Pipeline.Postfach) sent from the own address, or as fallback put as draft into Outlook; in Pipeline.Senden sent
// from the own address without the mailbox being read and without a draft (files up to 3 MB); otherwise sent from
// sales@, as far as mail_actions allows. Greeting and signature come from CodeTwo on the way out.
import { basisOf, fmtMenge, LOST_REASONS, OFFER_PHASE, OFFER_STAGES, VERPACKUNG_LABELS, VOLUMEN_BASIS_LABELS, VOLUMEN_FORM_LABELS } from "@pipeline/core";
import type { Activity, Attachment, Deal, DealMail, Outgoing, Volume } from "../api";
import { drawerDeal, loadDealDetail, openDeals, S, stageLabel, stages, viewHash, write } from "../state";
import { $, ago, cleanText, emptyState, esc, fmtDate, fmtDT, fmtKg, fmtSize, fmtTEUR, inputNumber, isTyping, num, parseDeNumber, redraw, toast } from "../util";
import { dealOptions, OFFER_LABEL } from "./board";
import { downloadAttachment, showOriginal } from "./original";
import { openDocument } from "./document";
import { bindRequirements, requirementsSection, resetRequirementsEdit } from "./requirements";
import { activityDetails, recordBox, taskBox } from "./tasks";
import { chanceHtml, fmtCredits, markenHtml, rechnungText, tablettenHtml, volumenZeilen } from "./volumen";

const VOL_SRC: Record<string, string> = { kunde: "vom Kunden", angebot: "aus Angebot", schaetzung: "KI-Schätzung", manuell: "manuell" };
const SOURCE: Record<string, string> = { manual: "manuell angelegt", hubspot: "aus HubSpot", anfrage: "aus Anfrage" };
const ASSIGNED: Record<string, string> = { thread: "Verlauf", ref: "Betreff-Nr.", ai: "KI", manual: "Manuell", import: "Import", anfrage: "Anfrage" };
const DOC_TYPE: Record<string, string> = {
  rezeptur: "Rezeptur",
  spezifikation: "Spezifikation",
  angebot: "Angebot",
  bestellung: "Bestellung",
  analysenzertifikat: "Analysenzertifikat",
  datenblatt: "Datenblatt",
  vertrag: "Vertrag",
  rechnung: "Rechnung",
  praesentation: "Präsentation",
  bild: "Bild",
  sonstiges: "Dokument",
};

/** The reply being written: to which mail, its text, the chosen files, and what became of it. */
const reply = {
  mailId: null as string | null,
  text: "",
  busy: false,
  /** One id per reply: a second click or a retried request sends nothing new (the server dedupes on it). */
  requestId: crypto.randomUUID(),
  result: null as Outgoing | null,
  files: new Set<string>(),
  uploads: [] as Attachment[],
};
const resetReply = () => Object.assign(reply, { mailId: null, text: "", busy: false, requestId: crypto.randomUUID(), result: null, files: new Set<string>(), uploads: [] });
const canReply = () => !S.readOnly && (S.mailActions.send !== "off" || (S.mailActions.ownMailbox && S.mailActions.drafts));
/** Files of one mail together: Exchange takes mails up to 35 MB, that is about 25 MB of files. */
const MAX_FILE = 25 * 1024 * 1024;

/** Files of the deal that can go along: attachments of its mails, files of its notes, uploads. */
function dealFiles(): Attachment[] {
  const seen = new Set<string>();
  return [...reply.uploads, ...S.dealMails.flatMap((m) => m.attachments), ...S.dealActivities.flatMap((a) => a.attachments)].filter((f) => !seen.has(f.id) && !!seen.add(f.id));
}

function filePicker(): string {
  const files = dealFiles();
  const list = files.length
    ? `<ul class="pickfiles">${files
        .map((f) => {
          const big = (f.sizeBytes ?? 0) > MAX_FILE;
          return `<li><label${big ? ` title="Größer als 25 MB"` : ""}><input type="checkbox" data-file="${esc(f.id)}" ${reply.files.has(f.id) ? "checked" : ""} ${big ? "disabled" : ""}> ${esc(f.filename ?? "Anhang")}${f.sizeBytes ? ` <span class="muted">${fmtSize(f.sizeBytes)}</span>` : ""}</label></li>`;
        })
        .join("")}</ul>`
    : "";
  return `<details class="files" ${reply.files.size ? "open" : ""}><summary>Anhänge${reply.files.size ? ` (${reply.files.size})` : ""}</summary>${list}
    <label class="btn upload">Datei hochladen<input type="file" id="reply-upload" multiple hidden></label> <span class="muted">zusammen bis 25 MB</span></details>`;
}

function replyPanel(m: DealMail): string {
  const a = S.mailActions;
  const r = reply.result;
  const own = (a.ownMailbox || a.sendOnly) && a.send !== "off";
  // A draft needs the mailbox read and written: only with Pipeline.Postfach.
  const draft = a.ownMailbox && a.drafts;
  const sales = a.send !== "off";
  const state = !r
    ? ""
    : r.status === "failed"
      ? `<p class="note warn">Nicht ausgeführt: ${esc(r.error ?? "unbekannter Fehler")}</p>`
      : r.status === "queued"
        ? `<p class="note">${r.kind === "draft" ? "Entwurf wird angelegt …" : "Wird gesendet …"}</p>`
        : r.status === "draft"
          ? `<p class="note ok">Entwurf liegt in deinem Postfach. ${r.webLink ? `<a href="${esc(r.webLink)}" target="_blank" rel="noopener">In Outlook öffnen</a> und dort senden.` : ""}</p>`
          : `<p class="note ok">Gesendet${r.test ? " (Testmodus: an die interne Testadresse)" : ""}. Die Mail erscheint gleich hier am Deal.</p>`;
  const to = m.direction === "in" ? m.fromName || m.fromAddr : "die Empfänger der Mail";
  const off = reply.busy ? "disabled" : "";
  return `<form class="reply" id="replyform">
    <label for="reply-text">Antwort an ${esc(to)}</label>
    <textarea id="reply-text" name="text" rows="8" ${reply.busy || r ? "disabled" : ""}>${esc(reply.text)}</textarea>
    ${r ? "" : `<p class="sig muted">Gruß und Signatur setzt CodeTwo beim Senden darunter.${a.sendOnly ? " Von deiner Adresse gehen Anhänge bis 3 MB zusammen mit." : ""}</p>${filePicker()}`}
    ${a.send === "test" ? `<p class="note warn">Testmodus: Gesendetes geht nur an die interne Testadresse.</p>` : ""}
    ${
      r && r.status !== "failed"
        ? `<div class="actions"><button type="button" class="btn" data-reply-close>Schließen</button></div>`
        : `<div class="actions"><button type="button" class="btn" data-reply-ai ${off}>${reply.busy ? "Bitte warten …" : "Entwurf von Claude"}</button>
      ${own ? `<button type="button" class="btn primary" data-reply-via="own" ${off} title="${a.ownMailbox ? `Von ${esc(S.me?.email ?? "")}, echte Antwort im Verlauf` : `Von ${esc(S.me?.email ?? "")}, als neue Mail mit dem Zitat; dein Postfach liest die Pipeline nicht. Anhänge bis 3 MB.`}">Von meiner Adresse senden</button>` : ""}
      ${sales ? `<button type="button" class="btn ${own ? "" : "primary"}" data-reply-via="sales" ${off}>Von sales@ senden</button>` : ""}
      <button type="button" class="btn" data-reply-close>Abbrechen</button></div>
      ${draft ? `<p class="fallback"><button type="button" class="link" data-reply-via="outlook" ${off}>Lieber in Outlook fertig machen</button> <span class="muted">(legt die Antwort als Entwurf in dein Postfach)</span></p>` : ""}`
    }
    ${state}
  </form>`;
}

/** Asks for the state until the worker is done (about a minute at most). */
async function followOutgoing(id: string): Promise<void> {
  for (let i = 0; i < 30; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    if (reply.result?.id !== id) return;
    try {
      reply.result = await S.api.outgoing(id);
    } catch {
      continue;
    }
    renderDrawer();
    if (reply.result.status !== "queued") return;
  }
}

export async function openDeal(id: string): Promise<void> {
  if (S.openId === id && !$("#drawer").hidden) return;
  S.openId = id;
  S.dealMails = [];
  S.dealActivities = [];
  S.dealContactOptions = [];
  S.dealTasks = [];
  S.dealRequirements = null;
  S.dealRequirementsAt = null;
  S.dealDocuments = [];
  S.dealAngeBot = null;
  resetRequirementsEdit();
  resetReply();
  askAnswer = "";
  askRun++;
  askBusy = false;
  $("#drawer").hidden = false;
  $("#scrim").hidden = false;
  // A deal opened (again) starts at the top; redraws of the open one keep the place (renderDrawer).
  delete $("#drawer").dataset.deal;
  renderDrawer();
  await loadDealDetail().catch(() => toast("Mails und Notizen des Deals konnten nicht geladen werden."));
  renderDrawer();
}

export function closeDrawer(push = true): void {
  S.openId = null;
  $("#drawer").hidden = true;
  $("#scrim").hidden = true;
  if (push && location.hash.startsWith("#deal-")) history.replaceState(null, "", `#${viewHash()}`);
}

function sparkline(hist: Volume["history"]): string {
  const pts = hist.filter((h) => num(h.kgPerYear));
  if (pts.length < 2) return "";
  const W = 480, H = 64, pl = 6, pr = 72, pt = 8, pb = 10;
  const max = Math.max(...pts.map((p) => p.kgPerYear!)) * 1.15;
  const x = (i: number) => pl + (i * (W - pl - pr)) / (pts.length - 1);
  const y = (v: number) => pt + (H - pt - pb) * (1 - v / max);
  let d = `M${x(0).toFixed(1)},${y(pts[0]!.kgPerYear!).toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) d += ` H${x(i).toFixed(1)} V${y(pts[i]!.kgPerYear!).toFixed(1)}`;
  const L = pts.length - 1;
  return `<svg class="spark" viewBox="0 0 ${W} ${H}" role="img" aria-label="Verlauf kg pro Jahr: ${pts.map((p) => fmtKg(p.kgPerYear)).join(", ")}">
    <line x1="${pl}" x2="${W - pr}" y1="${H - pb}" y2="${H - pb}" stroke="var(--line)" stroke-width="1"/>
    <path d="${d}" fill="none" stroke="var(--accent)" stroke-width="2" vector-effect="non-scaling-stroke"/>
    ${pts.map((p, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(p.kgPerYear!).toFixed(1)}" r="${i === L ? 3.5 : 2.5}" fill="${i === L ? "var(--accent)" : "var(--surface)"}" stroke="var(--accent)" stroke-width="1.5" vector-effect="non-scaling-stroke"/>`).join("")}
    <text x="${(x(L) + 8).toFixed(1)}" y="${(y(pts[L]!.kgPerYear!) + 3.5).toFixed(1)}">${fmtKg(pts[L]!.kgPerYear)}/J.</text>
  </svg>`;
}

/**
 * The AI's annual volume as its own section at the top of the deal (operator 03.11.2026: it was lost among the kg
 * figures): value in tablets, credits and place, forms and packaging as tiles, the calculation and Claude's reason.
 */
function jahresvolumenSection(d: Deal): string {
  const jv = d.jahresvolumen;
  const head = `<h3>Jahresvolumen <small>${jv?.at ? `Stand ${fmtDT(jv.at)}` : ""}</small></h3>`;
  if (!jv?.posten.length && !jv?.verpackung?.length)
    return `<div class="sec">${head}<p class="muted" style="margin:0">Noch keine Schätzung. Claude schätzt das Jahresvolumen mit dem nächsten Lagebild.</p></div>`;
  const rows = volumenZeilen(openDeals());
  const z = rows.find((r) => r.deal.id === d.id);
  const own = z ?? volumenZeilen([d])[0]!;
  const ranked = rows.filter((r) => r.rang !== null).length;
  const tile = (menge: string, label: string, how: string) => `<div><b>${esc(menge)}</b><span>${esc(label)} · ${esc(how)}</span></div>`;
  const tiles = [
    ...jv.posten.map((p) => tile(fmtMenge(p.menge, p.einheit), VOLUMEN_FORM_LABELS[p.form], p.beigestellt ? "beigestellt" : VOLUMEN_BASIS_LABELS[basisOf(p.basis)])),
    ...(jv.verpackung ?? []).map((p) => tile(fmtMenge(p.menge, "Stück"), VERPACKUNG_LABELS[p.art], VOLUMEN_BASIS_LABELS[basisOf(p.basis)])),
  ].join("");
  return `<div class="sec">${head}<div class="volbox jvbox">
      <p class="jv-head">${tablettenHtml(own.sterne)} <b>${own.credits === null ? "–" : fmtCredits(own.credits)} Credits</b> ${jv.chance ? chanceHtml(jv) : ""}${z?.rang ? ` · Platz ${z.rang} von ${ranked} nach Erwartung` : ""} · <a class="link" href="#volumen">Übersicht</a></p>
      <div class="jvstats">${tiles}</div>
      <p class="jv-calc">${esc(rechnungText(jv))}${own.potenzial !== null && own.credits !== null && own.potenzial > own.credits + 0.5 ? ` · Potenzial ohne Abschlag ${fmtCredits(own.potenzial)} Credits` : ""}</p>
      ${jv.begruendung ? `<p class="muted jv-why">Claude zur Menge: ${esc(jv.begruendung)}</p>` : ""}
      ${jv.chanceBegruendung ? `<p class="muted jv-why">Claude zur Chance: ${esc(jv.chanceBegruendung)}</p>` : ""}
    </div></div>`;
}

function volSection(d: Deal): string {
  const v = d.volume;
  const min = S.meta?.rules.minBatchKg ?? 50;
  const body =
    v && (num(v.kgPerYear) || num(v.kgPerBatch))
      ? `<div class="volstats">
        <div><b>${fmtKg(v.kgPerBatch)}</b><span>je Charge</span></div>
        <div><b>${num(v.batchesPerYear) ? v.batchesPerYear.toLocaleString("de-DE") : "–"}</b><span>Chargen pro Jahr</span></div>
        <div><b>${fmtKg(v.kgPerYear)}</b><span>pro Jahr</span></div>
        <div><b>${fmtTEUR(v.eurPerYear)}</b><span>Umsatz pro Jahr</span></div>
      </div>
      <p class="volbasis">${esc(v.units ?? "")}${v.basis ? ` · <span class="muted">${esc(v.basis)}</span>` : ""} <span class="pill p-acc">${esc(VOL_SRC[v.source] ?? v.source)}</span>${num(v.kgPerBatch) && v.kgPerBatch < min ? ` <span class="pill p-hoch">unter ${min} kg je Charge</span>` : ""}</p>
      ${sparkline(v.history)}
      ${v.history.length ? `<span class="eyebrow">Verlauf</span><ol class="volhist">${[...v.history].reverse().map((e) => `<li><span class="mono" style="font-size:12px">${fmtDate(e.at)}</span><span>${esc(e.note ?? "")}</span><span class="v">${fmtKg(e.kgPerYear)}/J.${num(e.eurPerYear) ? ` · ${fmtTEUR(e.eurPerYear)}` : ""}</span><span class="src">${esc(VOL_SRC[e.source] ?? e.source)}${e.mail ? ` · Mail „${esc(e.mail)}“` : ""}</span></li>`).join("")}</ol>` : ""}`
      : `<p class="muted" style="margin:0">Keine Angabe in kg oder Umsatz aus den Mails. Die KI trägt sie ein, sobald eine Mail sie nennt.</p>`;
  const form = S.readOnly
    ? ""
    : `<details class="volfix"><summary>Volumen korrigieren</summary><form id="volform" class="fform">
        <label><span>kg je Charge</span><input name="kgPerBatch" inputmode="decimal" value="${num(v?.kgPerBatch) ? inputNumber(v!.kgPerBatch) : ""}"></label>
        <label><span>Chargen pro Jahr</span><input name="batchesPerYear" inputmode="decimal" value="${num(v?.batchesPerYear) ? inputNumber(v!.batchesPerYear) : ""}"></label>
        <label><span>kg pro Jahr</span><input name="kgPerYear" inputmode="decimal" value="${num(v?.kgPerYear) ? inputNumber(v!.kgPerYear) : ""}"></label>
        <label><span>Umsatz pro Jahr (€)</span><input name="eurPerYear" inputmode="decimal" value="${num(v?.eurPerYear) ? inputNumber(v!.eurPerYear) : ""}"></label>
        <label class="wide"><span>Menge in Worten</span><input name="units" value="${esc(v?.units ?? "")}"></label>
        <label class="wide"><span>Anlass (Pflicht)</span><input name="note" required maxlength="300" placeholder="z. B. Telefonat mit dem Kunden am …"></label>
        <div class="fbtn"><button class="btn primary">Speichern</button></div></form></details>`;
  return `<div class="sec"><h3>Mengen aus den Mails <small>${v?.updatedAt ? `Stand ${fmtDT(v.updatedAt)}` : ""}</small></h3><div class="volbox">${body}</div>${form}</div>`;
}

const automatic = (m: DealMail) => ["ai", "thread", "ref"].includes(m.assignedBy ?? "");

function assignChip(m: DealMail): string {
  const t = ASSIGNED[m.assignedBy ?? ""] ?? m.assignedBy ?? "";
  if (!t) return "";
  const pct = m.assignedBy === "ai" && num(m.confidence) ? ` ${Math.round(m.confidence * 100)} %` : "";
  return `<span class="pill p-acc" title="${esc(m.reason ?? "")}">${esc(t)}${pct}</span>`;
}

function feedback(m: DealMail): string {
  if (m.feedback === "confirmed") return `<span class="pill p-ok">bestätigt</span>`;
  if (m.feedback === "corrected") return `<span class="pill p-info">korrigiert</span>`;
  if (S.readOnly) return "";
  const confirm = m.needsReview ? `<button class="btn" data-confirm title="Zuordnung aus dem Import stimmt">Bestätigen</button>` : "";
  const wrong = automatic(m) || m.needsReview ? `<button class="btn" data-wrong title="Die Mail gehört woanders hin">👎 falsch zugeordnet</button>` : "";
  return confirm + wrong;
}

function mailItem(m: DealMail): string {
  const ro = S.readOnly;
  const web = m.locations.find((l) => l.webLink)?.webLink;
  return `<li class="mail" data-mail="${esc(m.id)}" data-version="${m.version}">
    <div class="mh"><span><span class="dir">${m.direction === "out" ? "AUS" : "EIN"}</span> ${esc(m.fromName || m.fromAddr)}</span><span class="mono">${fmtDT(m.receivedAt)}</span></div>
    <div class="ms">${esc(m.subject)}</div>
    ${m.summary ? `<p class="mt">${esc(m.summary)}</p>` : ""}
    ${attachmentList(m.attachments)}
    <div class="mf">${assignChip(m)}${m.needsReview ? `<span class="pill p-mittel">zu prüfen</span>` : ""}${m.forwardedBy ? `<span class="pill p-info" title="über pipeline@">weitergeleitet von ${esc(m.forwardedBy)}</span>` : ""}${m.locations.some((l) => l.mailbox === "hubspot") ? `<span class="pill p-info">aus HubSpot</span>` : ""}${m.locations.some((l) => l.mailbox === "website") ? `<span class="pill p-info">Website-Formular</span>` : ""}${feedback(m)}
      ${m.hasOriginal ? `<button class="link" data-original>Original</button>` : ""}
      ${web ? `<a href="${esc(web)}" target="_blank" rel="noopener">In Outlook öffnen</a>` : ""}
      ${canReply() && reply.mailId !== m.id ? `<button class="link" data-reply>Antworten</button>` : ""}
      ${ro ? "" : `<select data-move aria-label="Umhängen"><option value="">Umhängen …</option><option value="__unclear">→ später zuordnen</option><option value="__none">→ kein Bezug (nicht speichern)</option>${dealOptions("", false)}</select>`}</div>
    ${reply.mailId === m.id ? replyPanel(m) : ""}
  </li>`;
}

function attachmentList(atts: Attachment[]): string {
  if (!atts.length) return "";
  return `<ul class="atts">${atts
    .map(
      (a) =>
        `<li><button class="link" data-att="${esc(a.id)}" data-name="${esc(a.filename ?? "anhang")}">📎 ${esc(a.filename ?? "Anhang")}</button> <span class="muted">${esc(DOC_TYPE[a.documentType ?? ""] ?? "")}${a.sizeBytes ? ` · ${fmtSize(a.sizeBytes)}` : ""}</span>${a.summary ? `<p>${esc(a.summary)}</p>` : ""}</li>`,
    )
    .join("")}</ul>`;
}

/** The last answer of "Frag Claude" for the open deal (kept across the refresh). */
let askAnswer = "";
/** Claude marks headings with **…**: shown bold, everything else as escaped text. */
const formatAnswer = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
let askBusy = false;
/** Counts the questions; opening another deal starts a new count, so its question box is free at once. */
let askRun = 0;

const ACTIVITY: Record<Activity["type"], string> = { note: "Notiz", call: "Anruf", meeting: "Termin", task: "Aufgabe" };
/** Expanded long activity texts, kept across the 60-second refresh. */
const expanded = new Set<string>();

/** Documents of the global Claude kept at this deal (overview, profile, preparation). */
function documentsSection(): string {
  const docs = S.dealDocuments;
  if (!docs.length) return "";
  return `<div class="sec"><h3>Dokumente von Claude <small>${docs.length}</small></h3><ul class="atts">${docs
    .map((x) => `<li><button class="link" data-doc-open="${esc(x.id)}">${esc(x.title)}</button><p>${esc(x.by)} · ${fmtDT(x.createdAt)}</p></li>`)
    .join("")}</ul></div>`;
}

function activitySection(): string {
  const items = S.dealActivities;
  if (!items.length) return "";
  return `<div class="sec"><h3>Notizen und Aktivitäten <small>${items.length} · neueste zuerst</small></h3><ul class="acts">${items
    .map((a) => {
      const body = cleanText(a.body);
      const long = body.length > 280 || body.split("\n").length > 5;
      const open = expanded.has(a.id);
      const planned = a.type === "meeting" && Date.parse(a.at) > Date.now() && !a.cancelledAt;
      const when = a.type === "task" && a.doneAt ? `erledigt ${fmtDT(a.doneAt)}` : fmtDT(a.at);
      const by = a.source === "outlook" && !a.calendarState ? `aus Outlook${a.calendars.length ? ` (${esc(a.calendars.map((c) => c.name ?? c.owner).join(", "))})` : ""}` : esc(a.author ?? "");
      return `<li class="act ${long && !open ? "clamp" : ""}"><div class="mh"><span><span class="dir">${ACTIVITY[a.type] ?? esc(String(a.type))}</span> ${by}</span><span class="mono">${planned ? `<span class="pill p-info">geplant</span> ` : ""}${when}</span></div>
        ${a.subject ? `<div class="ms">${esc(a.subject)}</div>` : ""}${activityDetails({ ...a, cancelled: !!a.cancelledAt })}${a.doneByAi ? `<div class="muted act-m">Von der KI geschlossen: ${esc(a.doneNote ?? "")}</div>` : ""}
        ${body ? `<div class="act-b">${esc(body)}</div>` : ""}${long ? `<button class="link" data-more="${esc(a.id)}">${open ? "Weniger" : "Ganzen Text"}</button>` : ""}${attachmentList(a.attachments ?? [])}</li>`;
    })
    .join("")}</ul></div>`;
}

/** The deal's contacts (one no longer with the company marked as such) and the picker for the company's others. */
function contactsLine(d: Deal, ro: boolean): string {
  const list = d.contacts
    .map((c) => `<span${c.hidden ? ` class="muted"` : ""}>${esc(c.name ?? "")}${c.role ? ` (${esc(c.role)})` : ""}${c.email ? ` <span class="mono" style="font-size:12px">${esc(c.email)}</span>` : ""}${c.hidden ? " · nicht mehr bei der Firma" : ""}</span>`)
    .join("<br>");
  const pick = ro || !S.dealContactOptions.length
    ? ""
    : `<select id="contact-add" aria-label="Ansprechpartner hinzufügen"><option value="">Ansprechpartner hinzufügen …</option>${S.dealContactOptions
        .map((c) => `<option value="${esc(c.id)}">${esc(c.name ?? c.email ?? "Ohne Namen")}${c.role ? ` (${esc(c.role)})` : ""}</option>`)
        .join("")}</select>`;
  return [list || (pick ? "" : "–"), pick].filter(Boolean).join("<br>");
}

function reasonSelect(d: Deal, ro: boolean): string {
  const reasons: string[] = [...LOST_REASONS];
  if (d.closedReason && !reasons.includes(d.closedReason)) reasons.push(d.closedReason);
  return `<select id="reason-sel" aria-label="Grund" ${ro ? "disabled" : ""}><option value="">Grund wählen …</option>${reasons
    .map((r) => `<option value="${esc(r)}" ${r === d.closedReason ? "selected" : ""}>${esc(r)}</option>`)
    .join("")}</select>`;
}

export function renderDrawer(): void {
  const dr = $("#drawer");
  // Do not throw away corrections that are being typed (the 60-second refresh renders again).
  // Not while typing in one of its forms (a click on a button there does not count: that must redraw).
  if (isTyping($("#drawer")) && document.activeElement?.closest("#req-form, #askform, #volform, .taskform, .recform, .tk-ctl, #replyform")) return;
  const d = drawerDeal();
  if (!d) {
    dr.innerHTML = `<div class="dr-h"><div class="dr-top"><h2>${S.loaded ? "Deal nicht gefunden" : "Lade …"}</h2><button class="xbtn" data-close aria-label="Schließen">×</button></div></div>`;
    delete dr.dataset.deal;
    return;
  }
  const ro = S.readOnly;
  // Whom a meeting can have: the deal's contacts still with the company, then the company's others.
  const people = [...d.contacts.filter((c) => !c.hidden), ...S.dealContactOptions];
  // The same deal drawn again (poll, a change) stays where the reader is; another one starts at the top.
  const same = dr.dataset.deal === d.id;
  dr.dataset.deal = d.id;
  redraw(
    dr,
    `
    <div class="dr-h">
      <div class="dr-top"><div><a class="co" href="#firma-${esc(d.companyId)}" title="Firmenseite öffnen">${esc(d.company)}</a><h2>${esc(d.name)}</h2></div><button class="xbtn" data-close aria-label="Schließen">×</button></div>
      <div class="dr-line">
        <select id="stage-sel" aria-label="Phase" ${ro ? "disabled" : ""}>${stages().map((s) => `<option value="${esc(s.id)}" ${s.id === d.stage ? "selected" : ""}>${esc(s.label)}</option>`).join("")}</select>
        ${d.stage === OFFER_PHASE ? `<select id="offer-sel" aria-label="Stand des Angebots" ${ro ? "disabled" : ""}>${OFFER_STAGES.map((o) => `<option value="${o}" ${o === d.offerStage ? "selected" : ""}>${OFFER_LABEL[o]}</option>`).join("")}</select>` : ""}
        ${d.stage === "verloren" ? reasonSelect(d, ro) : ""}
        <button class="ref" data-copyref title="Kürzel für den Mail-Betreff kopieren">[${esc(d.ref)}]</button>
        ${markenHtml({ pharma: d.profil?.pharma ?? false, jahresvolumen: d.jahresvolumen })}
        ${d.waitingOn === "us" ? `<span class="pill p-hoch">Wir sind dran</span>` : d.waitingOn === "customer" ? `<span class="pill p-info">Kunde ist dran</span>` : ""}
        ${d.health === "risk" ? `<span class="pill p-hoch">Risiko</span>` : d.health === "watch" ? `<span class="pill p-mittel">Beobachten</span>` : d.health === "ok" ? `<span class="pill p-ok">Im Plan</span>` : ""}
        ${d.needsReview ? `<span class="pill p-mittel">zu prüfen</span>${ro ? "" : `<button class="btn" data-reviewed>Prüfung abschließen</button>`}` : ""}
      </div>
    </div>
    <div class="dr-b">
      ${jahresvolumenSection(d)}
      ${volSection(d)}
      <div class="sec"><h3>KI-Lagebild <small>${d.summaryAt ? `Stand ${fmtDT(d.summaryAt)}` : ""}</small></h3>
        <div class="summary">${d.summary ? esc(d.summary) : `<span class="muted">Noch keine Zusammenfassung. Sie entsteht aus dem Mailverlauf.</span>`}</div></div>
      <div class="sec"><h3>Nächster Schritt und Aufgaben <small>die früheste offene Aufgabe ist der nächste Schritt</small></h3>
        ${taskBox({ dealId: d.id }, S.dealTasks, { empty: "Kein nächster Schritt." })}
        ${recordBox({ dealId: d.id }, people, S.meta?.calendar.enabled && S.meta.calendar.own ? d.contacts.filter((c) => !c.hidden && c.email) : undefined)}
      </div>
      <div class="sec"><h3>Eckdaten</h3><dl class="facts">
        <dt>Darreichungsform</dt><dd>${esc(d.product?.form || "–")}</dd>
        <dt>Packmittel</dt><dd>${esc(d.product?.pack || "–")}</dd>
        ${d.product?.notes ? `<dt>Produkt</dt><dd>${esc(d.product.notes)}</dd>` : ""}
        <dt>Verantwortlich</dt><dd>${ro ? esc(d.owner ?? "–") : `<select id="owner-sel" aria-label="Verantwortlich"><option value="">–</option>${S.users.map((u) => `<option value="${esc(u.id)}" ${u.id === d.ownerId ? "selected" : ""}>${esc(u.name)}</option>`).join("")}</select>`}</dd>
        <dt>Letzte Aktivität</dt><dd>${d.lastActivityAt ? `${fmtDT(d.lastActivityAt)} (${ago(d.lastActivityAt)})` : "–"}</dd>
        <dt>Angelegt</dt><dd>${fmtDate(d.createdAt)} · ${esc(SOURCE[d.source] ?? d.source)}</dd>
        <dt>Ansprechpartner</dt><dd>${contactsLine(d, ro)}</dd>
      </dl></div>
      <div class="sec"><h3>Frag Claude</h3>
        <form class="ask" id="askform"><input name="q" placeholder="z. B. Welche Dosierung hat der Kunde zuletzt genannt?" maxlength="2000" aria-label="Frage zum Deal"><button class="btn">Fragen</button></form>
        <div class="answer" id="answer" ${askAnswer ? "" : "hidden"}>${formatAnswer(askAnswer)}</div></div>
      ${requirementsSection(d)}
      ${documentsSection()}
      ${activitySection()}
      <div class="sec"><h3>Mails <small>${S.dealMails.length} · neueste zuerst</small></h3>
        ${S.dealMails.length ? `<ul class="mails">${S.dealMails.map(mailItem).join("")}</ul>` : emptyState("Noch keine Mails", "Mails zu diesem Deal erscheinen hier in zeitlicher Reihenfolge.")}
      </div>
    </div>`,
    ".dr-b",
    same,
  );
}

const mailOf = (el: Element) => {
  const li = el.closest<HTMLElement>("#drawer li.mail[data-mail]");
  return li ? { id: li.dataset.mail!, version: Number(li.dataset.version) } : null;
};
const dealOf = drawerDeal;

/** Event handlers of the drawer, registered once. */
export function bindDrawer(): void {
  bindRequirements(dealOf);
  $("#scrim").onclick = () => closeDrawer();
  addEventListener("keydown", (e) => {
    // Not while typing (Escape also dismisses autocomplete) and not with an unsent reply: closing would lose it.
    const unsent = !!(document.querySelector<HTMLTextAreaElement>("#reply-text")?.value ?? "").trim() && !reply.result;
    if (e.key === "Escape" && S.openId && !document.querySelector("dialog[open]") && !isTyping() && !unsent) closeDrawer();
  });
  document.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-open], #drawer [data-close], #drawer [data-copyref], #drawer [data-reviewed], #drawer [data-confirm], #drawer [data-wrong], #drawer [data-original], #drawer [data-att], #drawer [data-more], #drawer [data-reply], #drawer [data-reply-ai], #drawer [data-reply-via], #drawer [data-reply-close]",
    );
    const doc = (e.target as HTMLElement).closest<HTMLElement>("#drawer [data-doc-open]")?.dataset.docOpen;
    if (doc) return void openDocument(doc);
    if (!t) return;
    if (t.dataset.open !== undefined) return void (location.hash = `deal-${t.dataset.open}`);
    if (t.dataset.close !== undefined) return closeDrawer();
    if (t.dataset.more !== undefined) {
      if (!expanded.delete(t.dataset.more)) expanded.add(t.dataset.more);
      return renderDrawer();
    }
    const d = dealOf();
    if (!d) return;
    if (t.dataset.copyref !== undefined) {
      const txt = `[${d.ref}]`;
      try {
        await navigator.clipboard.writeText(txt);
        toast(`Kopiert: ${txt} – in den Mail-Betreff setzen`);
      } catch {
        toast(`Kürzel: ${txt}`);
      }
      return;
    }
    if (t.dataset.reviewed !== undefined) return void write(() => S.api.updateDeal(d.id, d.version, { needsReview: false }), "Prüfung abgeschlossen");
    if (t.dataset.att) return void downloadAttachment(t.dataset.att, t.dataset.name ?? "anhang");
    const m = mailOf(t);
    if (!m) return;
    if (t.dataset.original !== undefined) return void showOriginal(m.id);
    if (t.dataset.reply !== undefined) {
      resetReply();
      reply.mailId = m.id;
      renderDrawer();
      return void document.querySelector<HTMLTextAreaElement>("#reply-text")?.focus();
    }
    if (t.dataset.replyClose !== undefined) {
      resetReply();
      return renderDrawer();
    }
    if (t.dataset.replyAi !== undefined) {
      // A late draft only into the reply it was asked for: meanwhile another deal or mail may be open (a new reply
      // gets a new requestId), and its text would go to the wrong customer.
      const asked = reply.requestId;
      const still = () => reply.requestId === asked && reply.mailId === m.id && S.openId === d.id;
      reply.busy = true;
      renderDrawer();
      try {
        const text = await S.api.replyDraft(d.id, m.id);
        if (still()) reply.text = text;
      } catch (err) {
        if (still()) toast(err instanceof Error ? err.message : "Claude hat nicht geantwortet.");
      } finally {
        if (still()) {
          reply.busy = false;
          renderDrawer();
        }
      }
      return;
    }
    if (t.dataset.replyVia) {
      const via = t.dataset.replyVia as "outlook" | "sales";
      const text = (document.querySelector<HTMLTextAreaElement>("#reply-text")?.value ?? reply.text).trim();
      if (!text) return toast("Bitte erst den Text der Antwort schreiben.");
      if (reply.busy) return;
      reply.text = text;
      reply.busy = true;
      renderDrawer();
      let outgoingId: string | null = null;
      const sentFor = reply.requestId;
      const ok = await write(async () => {
        outgoingId = (await S.api.reply(d.id, { mailId: m.id, body: text, via, attachmentIds: [...reply.files], requestId: sentFor })).outgoingId;
      });
      // Another reply was opened meanwhile: it is sent all the same, but its state is not this one's.
      if (reply.requestId !== sentFor) return;
      reply.busy = false;
      if (ok && outgoingId) {
        reply.result = { id: outgoingId, kind: via === "outlook" ? "draft" : "send", status: "queued", test: false, webLink: null, error: null };
        renderDrawer();
        void followOutgoing(outgoingId);
      }
      return;
    }
    if (t.dataset.confirm !== undefined) return void write(() => S.api.decideMail(m.id, m.version, { action: "confirm" }), "Bestätigt");
    if (t.dataset.wrong !== undefined) {
      // Only the right target teaches the AI something, so ask for it.
      const sel = t.closest("li.mail")?.querySelector<HTMLSelectElement>("select[data-move]");
      sel?.focus();
      try {
        sel?.showPicker();
      } catch {
        // Not every browser opens a select programmatically; the focus and the hint are enough.
      }
      toast("Wohin gehört die Mail? Anderer Deal, „später zuordnen“ oder „kein Bezug“ im Menü „Umhängen“.");
    }
  });
  document.addEventListener("change", async (e) => {
    const t = e.target as HTMLSelectElement;
    const d = dealOf();
    if (!d) return;
    if (t.id === "stage-sel") return void write(() => S.api.updateDeal(d.id, d.version, { stageId: t.value }), `Phase: ${stageLabel(t.value)}`);
    if (t.id === "offer-sel") return void write(() => S.api.updateDeal(d.id, d.version, { offerStage: t.value }), `Angebot: ${OFFER_LABEL[t.value as keyof typeof OFFER_LABEL]}`);
    if (t.id === "reason-sel") return void write(() => S.api.updateDeal(d.id, d.version, { closedReason: t.value || null }), "Grund gespeichert");
    if (t.id === "owner-sel") return void write(() => S.api.updateDeal(d.id, d.version, { ownerId: t.value || null }), "Verantwortlich geändert");
    if (t.id === "contact-add" && t.value) return void write(() => S.api.addDealContact(d.id, t.value), "Ansprechpartner hinzugefügt");
    if (!t.matches("#drawer select[data-move]") || !t.value) return;
    const m = mailOf(t);
    if (!m) return;
    if (t.value === "__unclear") await write(() => S.api.decideMail(m.id, m.version, { action: "unklar" }), "Liegt jetzt unter „Mails zuordnen“");
    else if (t.value === "__none") await write(() => S.api.decideMail(m.id, m.version, { action: "kein_bezug" }), "Als ohne Bezug markiert. Die Mail bleibt nur im Postfach.");
    else await write(() => S.api.decideMail(m.id, m.version, { action: "assign", dealId: t.value }), "Umgehängt. Die KI lernt aus der Korrektur.");
  });
  // The reply text survives a re-render of the drawer.
  document.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    if (t.id === "reply-text") reply.text = (t as HTMLTextAreaElement).value;
  });
  document.addEventListener("change", async (e) => {
    const t = e.target as HTMLInputElement;
    if (t.matches("#replyform input[data-file]")) {
      if (t.checked) reply.files.add(t.dataset.file!);
      else reply.files.delete(t.dataset.file!);
      return renderDrawer();
    }
    if (t.id !== "reply-upload" || !t.files?.length || !S.openId) return;
    // The files belong to the deal and reply where the upload started, also if the user opens another deal meanwhile.
    const dealId = S.openId;
    const forReply = reply.requestId;
    const still = () => S.openId === dealId && reply.requestId === forReply;
    reply.busy = true;
    renderDrawer();
    for (const file of [...t.files]) {
      if (file.size > MAX_FILE) {
        toast(`${file.name} ist größer als 25 MB.`);
        continue;
      }
      try {
        const up = await S.api.upload(dealId, file);
        if (!still()) continue;
        reply.uploads.push({ ...up, documentType: null, summary: null });
        reply.files.add(up.id);
      } catch (err) {
        toast(err instanceof Error ? `${file.name}: ${err.message}` : `${file.name} konnte nicht hochgeladen werden.`);
      }
    }
    if (still()) reply.busy = false;
    renderDrawer();
  });
  document.addEventListener("submit", async (e) => {
    const f = e.target as HTMLFormElement;
    if (f.id === "askform") {
      e.preventDefault();
      const d = dealOf();
      const q = (f.elements.namedItem("q") as HTMLInputElement).value.trim();
      if (!d || q.length < 3 || askBusy) return;
      askBusy = true;
      const run = ++askRun;
      askAnswer = "";
      const box = $("#answer");
      box.hidden = false;
      box.textContent = "…";
      // The answer belongs to this deal: if another one is opened meanwhile, the rest of the stream is dropped.
      const here = () => S.openId === d.id && run === askRun;
      try {
        await S.api.ask(d.id, q, (t) => {
          if (!here()) return;
          askAnswer += t;
          const el = document.querySelector("#answer");
          if (el) el.innerHTML = formatAnswer(askAnswer);
        });
      } catch (err) {
        if (here()) {
          askAnswer = err instanceof Error ? err.message : "Claude hat nicht geantwortet.";
          const el = document.querySelector("#answer");
          if (el) el.textContent = askAnswer;
        }
      } finally {
        if (run === askRun) askBusy = false;
      }
      return;
    }
    if (f.id === "volform") {
      e.preventDefault();
      const d = dealOf();
      const val = (n: string) => (f.elements.namedItem(n) as HTMLInputElement).value.trim();
      const number = (n: string) => {
        const x = parseDeNumber(val(n));
        return val(n) && Number.isFinite(x) && x > 0 ? x : null;
      };
      if (!d) return;
      return void write(
        () => S.api.correctVolume(d.id, { kgPerBatch: number("kgPerBatch"), batchesPerYear: number("batchesPerYear"), kgPerYear: number("kgPerYear"), eurPerYear: number("eurPerYear"), units: val("units") || null, note: val("note") }),
        "Volumen korrigiert; der Eintrag steht im Verlauf.",
      );
    }
  });
}
